import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import type { ApiResponse } from "@/api/types";
import * as dev from "@/api/dev";

// ═══════════════════════════════════════════════════════════════════
// The resettable test members.
//
// Every account a developer could sign into had already finished onboarding,
// so onboarding, a new member's Home and every empty state could not be seen.
// `first-run` is a real account that goes back to any stage on demand, and
// `peer` is a second one it can wave at and talk to, so a wave and a
// conversation can be walked without touching a real member's data. Both are
// hidden from every real member and cannot reach one — the demo account never
// sees them, and they never see the demo account.
//
// No Lab sign-in is needed here: the dev key is the gate. The key is kept for
// this tab only.
// ═══════════════════════════════════════════════════════════════════

const PERSONA_NOTES: Record<dev.TestMemberKey, string> = {
  "first-run": "The new member: onboarding and every first-time screen.",
  peer: "A second member that first-run can wave at and talk to.",
};

const STAGE_NOTES: Record<dev.TestMemberStage, string> = {
  fresh: "The account only. Onboarding not started.",
  named: "Plus the name (onboarding step 2).",
  located: "Plus the city (step 3). Not complete.",
  "onboarded-empty": "Onboarding complete with one root, one language and a decade. Nothing else.",
  discoverable:
    "Onboarded, plus two Identity Board blocks: discovery unlocked, discoverable, and matched with the other test member.",
  "waved-at":
    "Discoverable, with a wave from the other test member waiting. Refused if the other one cannot wave yet.",
  "new-account": "No account. The next email-code sign-in is a real sign-up.",
};

interface OnboardingStatus {
  currentStep: number;
  isComplete: boolean;
  savedData: { name?: string; city?: string | null; originState?: string | null };
}

function readKey(): string {
  try {
    return sessionStorage.getItem("lab_dev_key") ?? "";
  } catch {
    return "";
  }
}

function keepKey(value: string) {
  try {
    sessionStorage.setItem("lab_dev_key", value);
  } catch {
    // Storage can be blocked; the page works without it.
  }
}

export default function TestMemberLab() {
  const { login, logout, user } = useAuth();
  const navigate = useNavigate();
  const [devKey, setDevKey] = useState(readKey);
  const [signInAfterReset, setSignInAfterReset] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [persona, setPersona] = useState<dev.TestMemberKey>("first-run");
  // Learned from each sign-in or reset: the test members' user ids.
  const [ids, setIds] = useState<Partial<Record<dev.TestMemberKey, string>>>({});
  const personaId = ids[persona] ?? null;
  const learn = (id: string) => setIds((known) => ({ ...known, [persona]: id }));

  const say = (m: string) => setLog((l) => [`${new Date().toLocaleTimeString()}  ${m}`, ...l].slice(0, 12));
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      say(await fn());
    } catch (e) {
      say("✘ " + (e instanceof Error ? e.message : "failed"));
    } finally {
      setBusy(false);
    }
  };

  const loadStatus = async () => {
    const res = await api.get<ApiResponse<OnboardingStatus>>("/onboarding/status");
    setStatus(res.data);
    return res.data;
  };

  const signIn = () =>
    run(async () => {
      const res = await dev.signInAsTestMember(devKey, persona);
      learn(res.data.user.id);
      await login(res.data.token);
      const s = await loadStatus();
      return `Signed in as ${res.data.user.email}${res.data.user.isNewUser ? " (created at fresh)" : ""}. Onboarding step ${s.currentStep}, complete: ${s.isComplete}.`;
    });

  const reset = (stage: dev.TestMemberStage) =>
    run(async () => {
      const res = await dev.resetTestMember(devKey, persona, stage);
      if (!res) {
        // new-account: nothing to sign in to, so drop the test member's session.
        if (personaId && user?.id === personaId) logout();
        setStatus(null);
        return `Reset ${persona} to new-account: the account and its email sign-in are gone.`;
      }
      learn(res.data.user.id);
      if (!signInAfterReset) return `Reset ${persona} to ${stage}.`;
      await login(res.data.token);
      const s = await loadStatus();
      return `Reset ${persona} to ${stage} and signed in. Onboarding step ${s.currentStep}, complete: ${s.isComplete}.`;
    });

  const signedInAs = (Object.keys(ids) as dev.TestMemberKey[]).find((key) => ids[key] === user?.id);
  const signedInAsTestMember = signedInAs !== undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Test member"
        subtitle="Real accounts you can put back at any stage. Hidden from every real member."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Test member" }]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
        <h2 className="text-sm font-medium text-gray-900">Which test member</h2>
        <div className="flex gap-2 flex-wrap">
          {dev.TEST_MEMBER_KEYS.map((key) => (
            <Button
              key={key}
              size="sm"
              variant={key === persona ? "primary" : "secondary"}
              onClick={() => setPersona(key)}
            >
              {key}
            </Button>
          ))}
        </div>
        <p className="text-xs text-gray-600">{PERSONA_NOTES[persona]}</p>
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <div>
          <h2 className="text-sm font-medium text-gray-900">Dev key</h2>
          <p className="text-xs text-gray-500">
            The server's DEV_API_KEY. Kept for this tab only. Refused on production.
          </p>
        </div>
        <input
          type="password"
          value={devKey}
          onChange={(e) => {
            setDevKey(e.target.value);
            keepKey(e.target.value);
          }}
          placeholder="X-Dev-Key"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full max-w-md"
        />
        <div className="flex gap-2 flex-wrap items-center">
          <Button onClick={signIn} disabled={!devKey || busy} isLoading={busy}>
            Sign in as {persona}
          </Button>
          {signedInAsTestMember && (
            <Button variant="secondary" onClick={() => navigate("/welcome")}>
              Go through the Lab as it
            </Button>
          )}
          <Badge variant={signedInAsTestMember ? "green" : "gray"}>
            {signedInAsTestMember ? `Lab is signed in as ${signedInAs} (${user?.email})` : "Lab is not signed in as a test member"}
          </Badge>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-medium text-gray-900">Reset {persona} to a stage</h2>
            <p className="text-xs text-gray-500">
              Removes everything it did, its waves and conversations included, then builds it back up
              through the real services.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={signInAfterReset}
              onChange={(e) => setSignInAfterReset(e.target.checked)}
            />
            Sign the Lab in after a reset
          </label>
        </div>
        <ul className="space-y-2">
          {dev.TEST_MEMBER_STAGES.map((stage) => (
            <li key={stage} className="flex items-center gap-3 flex-wrap">
              <Button
                size="sm"
                variant={stage === "new-account" ? "secondary" : "primary"}
                onClick={() => reset(stage)}
                disabled={!devKey || busy}
              >
                {stage}
              </Button>
              <span className="text-xs text-gray-600">{STAGE_NOTES[stage]}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-2">
        <h2 className="text-sm font-medium text-gray-900">Walk a conversation</h2>
        <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
          <li>Choose peer and reset it to discoverable.</li>
          <li>Choose first-run and reset it to waved-at. The Lab signs in as first-run.</li>
          <li>On Waves, wave back at Second Member, then "Say namaste".</li>
          <li>In Messages, read and write. Sign in as peer "as it is" to answer from the other side.</li>
        </ol>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="secondary" onClick={() => navigate("/lab/identity-board/waves")}>
            Waves
          </Button>
          <Button size="sm" variant="secondary" onClick={() => navigate("/lab/conversations")}>
            Messages
          </Button>
        </div>
      </section>

      {status && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-2">
          <h2 className="text-sm font-medium text-gray-900">GET /onboarding/status</h2>
          <p className="text-xs text-gray-600">
            Step {status.currentStep} · complete: {String(status.isComplete)} · name:{" "}
            {status.savedData.name ?? "—"} · city: {status.savedData.city ?? "—"} · roots:{" "}
            {status.savedData.originState ?? "—"}
          </p>
        </section>
      )}

      {log.length > 0 && (
        <section className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
          <ul className="space-y-1 font-mono text-xs text-gray-700">
            {log.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
